import { useSearchParams } from 'react-router-dom';

import { HMLoadError } from '@/modules/hiring-manager/components/HMLoadError';
import { toSearchSuffix } from '@/modules/hiring-manager/utils/searchSuffix';
import { useJobQuery } from '@/modules/jobs/queries/useJobQuery';
import {
  jobToRoleDraft,
  jobToWorkflowDraft,
  type SubmitIntent,
} from '@/modules/jobs/signal/jobRequestMappers';
import { useJobEditSubmit } from '@/modules/jobs/signal/useJobEditSubmit';
import Loader from '@/modules/ui/components/loader/Loader';

import { HMJobEditor } from './HMJobEditor';
import { useEditorInbound } from './useEditorInbound';

interface HMJobEditFormProps {
  jobId: string;
  /** After a save or publish succeeds; where the user goes next is the caller's. */
  onSaved: (intent: SubmitIntent) => void;
}

/**
 * `HMJobEditor` seeded from a saved job, for the edit page and the chat's side panel. Publish is Draft-only (PUT + kick
 * sourcing behind the confirmation); published jobs get "Save changes" only. The workflow is echoed from the loaded
 * job so the update mapper never wipes the settings the customer cannot see.
 *
 * The job is read again when the form opens, and the editor waits for it: the editor fills its fields once, and a
 * cached copy can predate a change made since, such as one Finlay made in chat, which saving would put back.
 */
export function HMJobEditForm({ jobId, onSaved }: HMJobEditFormProps) {
  const [searchParams] = useSearchParams();

  const searchSuffix = toSearchSuffix(searchParams);
  const jobQuery = useJobQuery(jobId, { refetchOnMount: 'always' });
  const editSubmit = useJobEditSubmit(jobQuery.data, onSaved);
  const editorInbound = useEditorInbound(jobQuery.data?.companyId ?? '', jobId);

  if (!jobQuery.isFetchedAfterMount || editorInbound.isLoading) {
    return <Loader />;
  }
  if (jobQuery.isError || !jobQuery.data) {
    return (
      <HMLoadError
        title="Job unavailable"
        description="We could not load this job. It may have been removed, or your connection dropped."
      />
    );
  }

  const job = jobQuery.data;

  return (
    <HMJobEditor
      // the editor seeds its draft once on mount; without the key, navigating between edit routes with cached
      // data would save one job's draft into another
      key={job.id}
      initialRole={jobToRoleDraft(job)}
      workflow={jobToWorkflowDraft(job)}
      canPublish={job.sourcingState === 'Draft'}
      automationPath={`/hm/jobs/${job.id}/automation${searchSuffix}`}
      integrationsPath={`/hm/integrations${searchSuffix}`}
      inbound={editorInbound.inbound}
      workspacePath={`/hm/workspace${searchSuffix}`}
      onSubmit={editSubmit.onSubmit}
      submitting={editSubmit.submitting}
      submitError={editSubmit.error}
    />
  );
}
